import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '@environment';
import {
  MultiplierSelected,
  PrizePackageMode,
  Question,
  QuestionType,
  QuizPrizePackage,
  Transaction,
  UserAction,
  injectCSSVariablesIntoDOM,
  FontTypes,
  emitIframeMessage,
  IframeMessage,
  Quiz,
} from '@longnecktech/splash-commons-fe';
import { LeaderboardService } from '@services/leaderboard.service';
import { NotificationService } from '@services/notification.service';
import { TranslationService } from '@services/translation.service';
import { PICK_QUESTION_TYPES } from '@shared/constants/game.consts';
import {
  DeepLinkingRequest,
  DeepLinkingResponse,
} from '@shared/types/deeplinking';
import { Leaderboard } from '@shared/types/leaderboard';
import { PicksRequest } from '@shared/types/submission-resource';
import { SubmitPicksResponse } from '@shared/types/submit-pick';
import { handleDeeplinkingResponse } from '@shared/utils/deeplinking-response.util';
import { overridePrizeLabel } from '@shared/utils/prize-label.utils';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import { CurrencyService } from './currency.service';
import { SessionService } from './session.service';
import { UserActionService } from './user-action.service';

@Injectable({
  providedIn: 'root',
})
export class GameService {
  private http = inject(HttpClient);
  private session = inject(SessionService);
  private currencyService = inject(CurrencyService);
  private notificationService = inject(NotificationService);
  private userActionService = inject(UserActionService);
  private sanitizer = inject(DomSanitizer);
  private translationService = inject(TranslationService);
  private leaderboardService = inject(LeaderboardService);

  private _isLoading = new BehaviorSubject<boolean>(false);

  isLoading$ = this._isLoading.asObservable();

  fetchGame(): Observable<Quiz> {
    return this.http
      .get<Quiz>(
        `${environment.backendUrl}/api/quiz/game?${this.getQueryParams()}`,
      )
      .pipe(
        tap((data: Quiz) => {
          this.removeGlobalLoader();
          const updatedLeaderboard = this.getLeaderboard(data.leaderboard);
          this.session.setCurrentGame({
            ...data,
            previousLeaderboards: this.mapPreviousLeaderboards(
              data.previousLeaderboards,
            ),
            leaderboard: updatedLeaderboard,
            prizePackage: this.getUpdatedPackage(data.prizePackage),
            questions: data.questions
              .map((question) => {
                if (question.type === QuestionType.CorrectScore) {
                  return {
                    ...question,
                    competitor1Score: 0,
                    competitor2Score: 0,
                    options: question.options.sort((a, b) => a.index - b.index),
                  };
                }
                return {
                  ...question,
                  options: question.options.sort((a, b) => a.index - b.index),
                };
              })
              .sort((a, b) => a.index - b.index),
          });
          this.leaderboardService.setSelectedLeaderboard(updatedLeaderboard);
          this.session.updateAuthToken();
          this.setCssVariables(data);
          emitIframeMessage(IframeMessage.GameLoaded);
        }),
      );
  }

  submitPicks(questions?: Question[]): Observable<SubmitPicksResponse | null> {
    if (!questions) return of(null);
    this._isLoading.next(true);
    return this.session.gameUUID$.pipe(
      take(1),
      withLatestFrom(
        this.session.jokerSelected$,
        this.session.multiplierSelected$,
      ),
      map(([gameUuid, jokerSelected, multiplierSelected]) => {
        const picks = this.getPicks(
          questions,
          jokerSelected,
          multiplierSelected,
        );
        return { picks, gameUuid };
      }),
      switchMap(({ picks, gameUuid }) => {
        let params = new HttpParams();
        const sessionId = this.session.sessionParams.sessionId;

        if (sessionId) {
          params = params.set('sessionId', sessionId);
        }

        return this.http.post<SubmitPicksResponse>(
          environment.backendUrl + `/api/quiz/pick/v2/${gameUuid}`,
          picks,
          { params },
        );
      }),
      tap((res) => {
        this.session.updateDataOnSubmittingPicks(false, res.txUuid);
        this._isLoading.next(false);
      }),
      catchError((err: HttpErrorResponse) => this.handleError(err)),
    );
  }

  updatePicks(
    transaction: Transaction,
    questions?: Question[],
  ): Observable<SubmitPicksResponse | null> {
    if (!questions) return of(null);
    this._isLoading.next(true);
    return this.session.jokerSelected$.pipe(
      take(1),
      withLatestFrom(this.session.multiplierSelected$),
      map(([jokerSelected, multiplierSelected]) => {
        return this.getPicks(questions, jokerSelected, multiplierSelected);
      }),
      switchMap((picks) => {
        let params = new HttpParams();
        const sessionId = this.session.sessionParams.sessionId;

        if (sessionId) {
          params = params.set('sessionId', sessionId);
        }

        return this.http.put<SubmitPicksResponse>(
          environment.backendUrl + `/api/quiz/pick/v2/${transaction.uuid}`,
          picks,
          { params },
        );
      }),
      tap((res) => {
        this.session.updateDataOnSubmittingPicks(true, res.txUuid);
        this._isLoading.next(false);
      }),
      catchError((err: HttpErrorResponse) => this.handleError(err)),
    );
  }

  sendBets(
    request: DeepLinkingRequest,
    gameUuid: string,
  ): Observable<DeepLinkingResponse> {
    this._isLoading.next(true);
    this.userActionService
      .sendAction(
        UserAction.CLICK_DEEPLINKING,
        {
          bets: request.Bets,
        },
        this.session.game()!.uuid,
      )
      .pipe(take(1))
      .subscribe();
    return this.http
      .post<DeepLinkingResponse>(
        environment.backendUrl + '/api/quiz/deeplinking/' + gameUuid,
        request,
      )
      .pipe(
        tap(() => this._isLoading.next(false)),
        tap((res) =>
          handleDeeplinkingResponse(res, request.Bets, this.sanitizer),
        ),
        catchError((err) => {
          this._isLoading.next(false);
          this.notificationService.showNotification('deeplink.failed');
          return throwError(() => new Error(err.message));
        }),
      );
  }

  private handleError(err: HttpErrorResponse): Observable<never> {
    const message =
      err.status === 400 && err.error.message
        ? err.error.message
        : this.translationService.getTranslation('game.submitError');
    this.notificationService.showNotification(message);
    this._isLoading.next(false);
    this.userActionService
      .sendAction(
        UserAction.TRANSACTION_ERROR,
        {
          when: 'submitting picks',
          error: message,
        },
        this.session.game()!.uuid,
      )
      .pipe(take(1))
      .subscribe();
    return throwError(() => new Error(err.message));
  }

  private mapPreviousLeaderboards(
    previousLeaderboards: Leaderboard[],
  ): Leaderboard[] {
    if (!previousLeaderboards) return [];
    return previousLeaderboards.map((lb) => ({
      ...lb,
      prizePackage: lb.prizePackage
        ? {
            ...lb.prizePackage,
            prizes: lb.prizePackage.prizes
              .map((prize) => ({
                ...prize,
                prize: {
                  ...prize.prize,
                  label: overridePrizeLabel(prize.prize, this.currencyService),
                },
              }))
              .slice()
              .sort((a, b) => a.score - b.score),
          }
        : lb.prizePackage,
    }));
  }

  private getLeaderboard(leaderboard: Leaderboard | null): Leaderboard | null {
    if (!leaderboard) return null;
    const prizePackage = leaderboard.prizePackage
      ? {
          ...leaderboard.prizePackage,
          prizes: leaderboard.prizePackage.prizes
            .map((prize) => ({
              ...prize,
              prize: {
                ...prize.prize,
                label: overridePrizeLabel(prize.prize, this.currencyService),
              },
            }))
            .slice()
            .sort((a, b) => a.score - b.score),
        }
      : leaderboard.prizePackage;
    try {
      return {
        ...leaderboard,
        prizePackage,
      };
    } catch (e) {
      console.error("Can't update leaderboard", e);
      return null;
    }
  }

  private getUpdatedPackage(prizePackage: QuizPrizePackage): QuizPrizePackage {
    try {
      if (!prizePackage?.prizes) {
        return prizePackage;
      }

      const updatedPrizes = prizePackage.prizes.map((prize) => {
        if (typeof prize.prize.value === 'number' && prize.prize.currency) {
          return {
            ...prize,
            prize: {
              ...prize.prize,
              label: overridePrizeLabel(prize.prize, this.currencyService),
            },
          };
        }
        return prize;
      });

      const isPointsMode = prizePackage.mode === PrizePackageMode.Points;
      const sortedPrizes = [...updatedPrizes].sort((a, b) => {
        if (!isPointsMode) {
          return b.score - a.score;
        }
        return b.maxValue - a.maxValue;
      });

      return {
        ...prizePackage,
        prizes: sortedPrizes,
      };
    } catch (e) {
      console.error("Can't update prize package", e);
      return prizePackage;
    }
  }

  private getPicks(
    questions: Question[],
    jokerSelected: string | undefined,
    multiplierSelected: MultiplierSelected,
  ): PicksRequest {
    return questions.reduce((acc, question) => {
      if (PICK_QUESTION_TYPES.includes(question.type)) {
        const optionUuids = question.options
          .filter((option) => option.isPicked)
          .map((option) => option.uuid);
        if (optionUuids.length) {
          acc.push({
            questionUuid: question.uuid,
            optionUuids,
            multiplierOptionUuid:
              multiplierSelected.questionUuid === question.uuid &&
              multiplierSelected.optionUuid
                ? multiplierSelected.optionUuid
                : undefined,
          });
        }
        if (jokerSelected === question.uuid) {
          acc.push({
            questionUuid: question.uuid,
            optionUuids: [],
            isJokerUsed: true,
          });
        }
      }

      if (question.type === QuestionType.CorrectScore) {
        acc.push({
          questionUuid: question.uuid,
          competitor1Score: question.competitor1Score,
          competitor2Score: question.competitor2Score,
        });
      }

      if (question.type === QuestionType.NumberSelect) {
        acc.push({
          questionUuid: question.uuid,
          numberSelectValue: question.numberSelectValue,
        });
      }

      return acc;
    }, [] as PicksRequest);
  }

  private getQueryParams(): string {
    const params: { instance?: string; gameUuid?: string } = {};
    if (this.session.sessionParams.instance)
      params['instance'] = this.session.sessionParams.instance;
    if (this.session.sessionParams.gameUuid)
      params['gameUuid'] = this.session.sessionParams.gameUuid;
    return new URLSearchParams(params).toString();
  }

  private setCssVariables(game: Quiz): void {
    try {
      const styleEl = document.createElement('style');
      styleEl.textContent = injectCSSVariablesIntoDOM(game.theme, {
        defaultFonts: {
          [FontTypes.Regular]:
            '/assets/fonts/Montserrat/static/Montserrat-Regular.ttf',
          [FontTypes.Medium]:
            '/assets/fonts/Montserrat/static/Montserrat-Medium.ttf',
          [FontTypes.Bold]:
            '/assets/fonts/Montserrat/static/Montserrat-Bold.ttf',
          [FontTypes.Headlines]:
            '/assets/fonts/Montserrat/static/Montserrat-Regular.ttf',
        },
        defaultSecondaryCtaButtonColors: {
          '--z-cta-buttons-outline-background-color': 1,
          '--color-accents-1': 2,
        },
        defaultCtaBorderRadius: 28,
        customMaterialVariables: `
        --mat-option-selected-state-label-text-color: #000000;
        --mat-pseudo-checkbox-minimal-selected-checkmark-color: #000000;
      `,
      });
      document.head.appendChild(styleEl);
    } catch (e) {
      console.error("Can't set game theme", e);
    }
  }

  private removeGlobalLoader(): void {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('global-loader');
    if (el) {
      el.remove();
    }
  }
}
